import { AxiosInstance } from "axios"
import { LineageResponse, QnrMetadataResponse } from "types/lineage.types"
import { ProjectAncestryResponse } from "types/ancestry.types"

/**
 * Fetches the lineage graph of a query session: the variables produced by the transforms
 * run on the given query instance, and what each one derives from. Authentication is handled by the
 * axios instance.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceId - The live query instance (from InitialiseDataReporting)
 */
export async function getQueryLineage(
  axiosInstance: AxiosInstance,
  instanceId: string
): Promise<LineageResponse> {
  return axiosInstance.get(`/query-lineage-get?instanceId=${encodeURIComponent(instanceId)}`)
}

/**
 * Project-mode lineage: the server loads the project's data into a fresh instance, replays the
 * named saved query, and returns the resulting DAG — no live console session needed. With no
 * query name, only collected variables exist (an empty derivation DAG).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param projectId - The project
 * @param owner - Owner hint (may be "" — resolved server-side)
 * @param queryName - The saved query to replay (optional)
 */
export async function getProjectLineage(
  axiosInstance: AxiosInstance,
  projectId: string,
  owner: string,
  queryName?: string
): Promise<LineageResponse> {
  const queryParam = queryName ? `&query=${encodeURIComponent(queryName)}` : ""
  return axiosInstance.get(
    `/query-lineage-get?id=${encodeURIComponent(projectId)}&hint_owner=${encodeURIComponent(owner)}${queryParam}`
  )
}

/**
 * Fetches the questionnaire's field definitions (type + code frame) and the drift of the loaded
 * data's metadata from them — used to show a lineage leaf's design provenance inline.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param projectId - The project
 * @param owner - Owner hint (may be "" — resolved server-side)
 */
export async function getQnrMetadata(
  axiosInstance: AxiosInstance,
  projectId: string,
  owner: string
): Promise<QnrMetadataResponse> {
  return axiosInstance.get(
    `/questionnaire-metadata-get?id=${encodeURIComponent(projectId)}&hint_owner=${encodeURIComponent(owner)}`
  )
}

/**
 * Fetches a deliverable's captured lineage DAG by its content hash — the derivation graph that
 * was snapshotted when the deliverable was recorded (see the `lineage` field on
 * `deliverable.exported` audit records).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param projectId - The project
 * @param owner - Owner hint (may be "" — resolved server-side)
 * @param hash - The DAG's content hash from the audit record
 */
export async function getDeliverableLineage(
  axiosInstance: AxiosInstance,
  projectId: string,
  owner: string,
  hash: string
): Promise<LineageResponse> {
  return axiosInstance.get(
    `/deliverable-lineage-get?id=${encodeURIComponent(projectId)}&hint_owner=${encodeURIComponent(owner)}&hash=${encodeURIComponent(hash)}`
  )
}

/**
 * Cross-project ancestry: which projects this one was built FROM, transitively — the
 * merges and wave forks behind it. Walks the audit chains backwards server-side.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param projectId - The project to start from
 * @param owner - Owner hint (may be "" — resolved server-side)
 * @param depth - How many generations to walk (server clamps to 1..16, default 6)
 */
export async function getProjectAncestry(
  axiosInstance: AxiosInstance,
  projectId: string,
  owner: string,
  depth?: number
): Promise<ProjectAncestryResponse> {
  const depthParam = depth ? `&depth=${depth}` : ""
  return axiosInstance.get(
    `/project-ancestry-get?id=${encodeURIComponent(projectId)}&hint_owner=${encodeURIComponent(owner)}${depthParam}`
  )
}
